import { ArchiveIcon, CartIcon, ChevronDownIcon, CopyIcon, EyeIcon, PenIcon } from "@xola/icons";
import { Badge, flash, PopoverList, Table, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { isPartnerPackageProduct } from "common";
import { sortBy, startCase, truncate } from "lodash";
import pluralize from "pluralize";
import React, { useMemo, useState } from "react";
import { useMutation } from "react-query";
import { Button, ButtonType, CheckoutType, PopupCheckoutType, Product, ProductObject, Seller } from "types";
import { PartnerBadge, PartnerSellerBadge } from "../PartnerBadge";
import { api } from "@/api/api";
import { InlineConfirmDeletePopover } from "@/components/Common/InlineConfirmDeletePopover";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { ProductImage } from "@/components/Common/ProductImage";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useSeller, useUser } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";
import { getButtonCode, getButtonUrl, isX2CheckoutSupported, openCheckoutIframe } from "@/models/ButtonCode";
import { getExperienceFromQueryCache } from "@/models/Experience";
import { hasX2CheckoutWithFullProbability } from "@/models/Seller";
import { isXolaAdmin } from "@/models/User";
import { getSeller } from "@/utils/getSeller";

interface ButtonCodeTableProps {
    isSearch?: boolean;
    showSystemButtons: boolean;
    products?: Product[];
    buttons: Button[];
    popupType: PopupCheckoutType;
    onEditButton: (button: Button) => void;
}

export const ButtonCodeTable = ({
    isSearch = false,
    products,
    buttons,
    popupType,
    showSystemButtons,
    onEditButton,
}: ButtonCodeTableProps) => {
    const checkoutTypePopover = useModal();
    const [selectedCheckoutType, setSelectedCheckoutType] = useState<CheckoutType>(CheckoutType.POPUP);
    const seller = getSeller();

    const handleCheckoutTypeSelect = (type: CheckoutType) => {
        setSelectedCheckoutType(type);
        setTimeout(checkoutTypePopover.hide, 0);
    };

    return (
        <div className="w-full">
            <Table className="w-full table-fixed">
                <Table.Head>
                    <Table.Row>
                        <Table.Header>{showSystemButtons ? "Product" : "Button Name"}</Table.Header>

                        <Table.Header>Type</Table.Header>

                        <CheckoutTypeHeader
                            selectedCheckoutType={selectedCheckoutType}
                            checkoutTypePopover={checkoutTypePopover}
                            onSelect={handleCheckoutTypeSelect}
                        />

                        <Table.Header className="text-right">Actions</Table.Header>
                    </Table.Row>
                </Table.Head>
            </Table>

            <div className="max-h-[500px] overflow-y-auto">
                <Table className="w-full table-fixed">
                    <Table.Body className="w-full table-fixed">
                        {showSystemButtons ? (
                            <>
                                {!isSearch && (
                                    <SellerTableRow
                                        key={seller.id}
                                        type={ButtonType.ALL_PRODUCTS}
                                        checkoutType={selectedCheckoutType}
                                        popupType={popupType}
                                    />
                                )}
                                {products?.map((product) => (
                                    <ProductTableRow
                                        key={product.id}
                                        product={product}
                                        type={ButtonType.SINGLE}
                                        checkoutType={selectedCheckoutType}
                                        popupType={popupType}
                                    />
                                ))}
                            </>
                        ) : (
                            sortBy(buttons, "name").map((button) => (
                                <ButtonTableRow
                                    key={button.id}
                                    button={button}
                                    type={button.type}
                                    checkoutType={selectedCheckoutType}
                                    popupType={popupType}
                                    onEditButton={onEditButton}
                                />
                            ))
                        )}
                    </Table.Body>
                </Table>
            </div>
        </div>
    );
};

const CheckoutTypeHeader = ({
    selectedCheckoutType,
    checkoutTypePopover,
    onSelect,
}: {
    selectedCheckoutType: CheckoutType;
    checkoutTypePopover: ReturnType<typeof useModal>;
    onSelect: (type: CheckoutType) => void;
}) => {
    return (
        <Table.Header>
            <span className="flex cursor-pointer items-center underline" onClick={checkoutTypePopover.toggle}>
                <PopoverList
                    trigger="click"
                    visible={checkoutTypePopover.isVisible}
                    onClickOutside={checkoutTypePopover.hide}
                >
                    <span className="space-x-0.5">
                        <span>
                            {selectedCheckoutType === CheckoutType.POPUP && "Drawer Code"}
                            {selectedCheckoutType === CheckoutType.EMBEDDED && "Embed Code"}
                            {selectedCheckoutType === CheckoutType.URL && "Link Code"}
                        </span>
                        <ChevronDownIcon />
                    </span>

                    <PopoverList.Item name="popup" onClickItem={() => onSelect(CheckoutType.POPUP)}>
                        Drawer
                    </PopoverList.Item>

                    <PopoverList.Item name="embedded" onClickItem={() => onSelect(CheckoutType.EMBEDDED)}>
                        Embed
                    </PopoverList.Item>

                    <PopoverList.Item name="url" onClickItem={() => onSelect(CheckoutType.URL)}>
                        Link
                    </PopoverList.Item>
                </PopoverList>
            </span>
        </Table.Header>
    );
};

interface ProductTableRowProps {
    product: Product;
    type?: ButtonType;
    checkoutType: CheckoutType;
    popupType: PopupCheckoutType;
}

const ProductTableRow = ({ product, type, checkoutType, popupType }: ProductTableRowProps) => {
    const seller = useSeller();
    const showX2Checkout = isX2CheckoutSupported(undefined, product);
    const customType = showX2Checkout ? `${type}_x2` : type;
    const buttonCode = getButtonCode(checkoutType, popupType, customType, product);
    const isPartnerProduct = product?.object === ProductObject.EXPERIENCE && product?.seller.id !== seller.id;
    const isPartnerPackage = isPartnerPackageProduct(product);
    const partnerSellerId = isPartnerProduct || isPartnerPackage ? product.seller.id : null;

    return (
        <TableRow
            type={type}
            checkoutType={checkoutType}
            buttonCode={buttonCode}
            showX2Checkout={!!showX2Checkout}
            item={product}
            name={
                <div className="flex items-center space-x-2">
                    {type === ButtonType.SINGLE && <ProductImage size="medium" className="mr-0" product={product} />}
                    <div className="flex flex-col space-y-2">
                        <NoTranslate as="p" className="truncate whitespace-normal text-base font-bold">
                            {truncate(product.name, { length: 100 })}
                        </NoTranslate>
                        {(isPartnerProduct || isPartnerPackage) && (
                            <div className="flex items-center gap-2">
                                <PartnerBadge isPackage={isPartnerPackage} />
                                {partnerSellerId && <PartnerSellerBadge sellerId={partnerSellerId} />}
                            </div>
                        )}
                    </div>
                </div>
            }
        />
    );
};

const SellerTableRow = ({ type, checkoutType, popupType }: Omit<ProductTableRowProps, "product">) => {
    const seller = useSeller();
    const showX2Checkout = hasX2CheckoutWithFullProbability();
    const customType = showX2Checkout ? `${type}_x2` : type;
    const buttonCode = getButtonCode(checkoutType, popupType, customType);

    return (
        <TableRow
            type={type}
            checkoutType={checkoutType}
            buttonCode={buttonCode}
            showX2Checkout={showX2Checkout}
            item={seller}
            name={
                <div className="flex items-center space-x-2">
                    <div className="flex flex-col space-y-2">
                        <NoTranslate as="p" className="truncate whitespace-normal text-base font-bold">
                            All Products
                        </NoTranslate>
                    </div>
                </div>
            }
        />
    );
};

interface ButtonTableRowProps {
    button: Button;
    type?: ButtonType;
    checkoutType: CheckoutType;
    popupType: PopupCheckoutType;
    onEditButton: (button: Button) => void;
}

const ButtonTableRow = ({ button, type, checkoutType, popupType, onEditButton }: ButtonTableRowProps) => {
    const autoApplyCode = button?.autoApplyCode;
    const showX2Checkout = isX2CheckoutSupported(button, undefined);
    const customType = showX2Checkout ? `${type}_x2` : type;
    const buttonCode = getButtonCode(checkoutType, popupType, customType, undefined, button);

    const experienceNameList = button.items.map((item) => {
        return getExperienceFromQueryCache(item.product.id)?.name?.trim() ?? "";
    });

    const experienceNames = useMemo(
        () => experienceNameList.map((name) => truncate(name, { length: 40 })),
        [experienceNameList],
    );

    return (
        <TableRow
            type={type}
            checkoutType={checkoutType}
            buttonCode={buttonCode}
            showX2Checkout={!!showX2Checkout}
            item={button}
            name={
                <div className="flex items-center space-x-2">
                    <div className="flex flex-col space-y-2">
                        <NoTranslate as="p" className="truncate whitespace-normal text-base font-bold">
                            {truncate(button.name.trim(), { length: 100 })}
                        </NoTranslate>
                        <div className="flex items-center gap-2">
                            <span className="min-w-20">
                                {button.items.length > 0 && (
                                    <Tooltip
                                        placement="bottom-start"
                                        content={<span className="whitespace-pre">{experienceNames.join("\n")}</span>}
                                    >
                                        <Badge color="secondary" className="cursor-default">
                                            {button.items.length} {pluralize("product", button.items.length)}
                                        </Badge>
                                    </Tooltip>
                                )}
                            </span>
                        </div>
                        {autoApplyCode && (
                            <Tooltip content="Auto-apply code for this button" placement="left">
                                <p className="cursor-default truncate text-sm">Code: {autoApplyCode.toUpperCase()}</p>
                            </Tooltip>
                        )}
                    </div>
                </div>
            }
            onEdit={() => onEditButton(button)}
        />
    );
};

interface TableRowProps {
    type?: ButtonType;
    checkoutType: CheckoutType;
    buttonCode: string;
    showX2Checkout: boolean;
    item: Seller | Product | Button;
    name: React.ReactNode;
    onEdit?: () => void;
}

const TableRow = ({ type, checkoutType, buttonCode, showX2Checkout, item, name, onEdit }: TableRowProps) => {
    const user = useUser();
    const seller = useSeller();
    const invalidate = useInvalidate();

    const { mutateAsync: archiveButton, isLoading: isArchiving } = useMutation(async (buttonId: ID) => {
        await api.delete(`/buttons/${buttonId}`);
        invalidate("buttons", buttonId);
        flash.success("Successfully archived button");
    });

    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(buttonCode);
        if (checkoutType === CheckoutType.URL) {
            flash.success(`Checkout URL copied to clipboard`);
        } else {
            flash.success(`Button Code copied to clipboard`);
        }
    };

    const handlePreviewClick = () => {
        const url = getButtonUrl(seller, checkoutType, type, item as Product, item as Button);
        if (url) {
            window.open(url, "_blank");
        }
    };

    const isEmbedded = checkoutType === CheckoutType.EMBEDDED;
    const handleX2PreviewClick = () => {
        const url = getButtonUrl(seller, checkoutType, String(type) + "_x2", item as Product, item as Button);
        if (isEmbedded || (checkoutType === CheckoutType.URL && url)) {
            window.open(url, "_blank");
        } else {
            openCheckoutIframe(url);
        }
    };

    const handleArchiveClick = async (buttonId: ID) => {
        await archiveButton(buttonId);
        invalidate("buttons", buttonId);
    };

    const isButton = "object" in item && item.object === "button";
    let displayType;

    if (isButton) {
        if (item.type === ButtonType.CHECKOUT) {
            displayType = "Grid";
        } else if (item.type === ButtonType.GIFT) {
            displayType = "Gifts";
        } else {
            displayType = startCase(item.type.toLowerCase());
        }
    } else if (type === ButtonType.ALL_PRODUCTS) {
        displayType = "All Products";
    } else if ("object" in item && item.object) {
        displayType = formatObjectToType(item.object);
    }

    return (
        <Table.Row className="border-b border-gray-light">
            <Table.Cell>{name}</Table.Cell>
            {displayType && <Table.Cell>{displayType}</Table.Cell>}
            <Table.Cell className="no-translate text-sm! font-mono text-gray-darker">
                <NoTranslate as="div" className="whitespace-normal break-all text-sm">
                    <span title="Click to copy" className="cursor-copy" onClick={handleCopyToClipboard}>
                        {buttonCode}
                    </span>
                </NoTranslate>
            </Table.Cell>
            <Table.Cell className="text-right">
                <div className="flex items-center justify-end space-x-1">
                    {showX2Checkout && (
                        <ActionButton
                            data-testid="button-item-x2-preview-button"
                            title="Preview in X2 Checkout"
                            onClick={handleX2PreviewClick}
                        >
                            <CartIcon />
                        </ActionButton>
                    )}
                    {(isXolaAdmin(user) || !showX2Checkout) && (
                        <ActionButton
                            data-testid="button-item-preview-button"
                            title="Preview"
                            onClick={handlePreviewClick}
                        >
                            <EyeIcon />
                        </ActionButton>
                    )}
                    {onEdit && (
                        <ActionButton data-testid="button-item-edit-button" title="Edit" onClick={onEdit}>
                            <PenIcon />
                        </ActionButton>
                    )}
                    <ActionButton
                        data-testid="button-item-copy-button"
                        title="Click to copy"
                        className="cursor-copy"
                        onClick={handleCopyToClipboard}
                    >
                        <CopyIcon />
                    </ActionButton>
                    {type !== ButtonType.SINGLE && type !== ButtonType.ALL_PRODUCTS && (
                        <InlineConfirmDeletePopover
                            placement="left"
                            confirmButtonText="Confirm"
                            onConfirm={async () => handleArchiveClick(item.id)}
                        >
                            <ActionButton
                                data-testid="button-item-archive-button"
                                title="Archive"
                                disabled={isArchiving}
                            >
                                <ArchiveIcon />
                            </ActionButton>
                        </InlineConfirmDeletePopover>
                    )}
                </div>
            </Table.Cell>
        </Table.Row>
    );
};

interface ActionButtonProps {
    disabled?: boolean;
    title?: string;
    className?: string;
    children?: React.ReactNode;
    "data-testid"?: string;
    onClick?: () => void;
}

const ActionButton = ({ disabled, title, className, onClick, children, ...rest }: ActionButtonProps) => {
    return (
        <Tooltip content={<span className="text-sm">{title}</span>}>
            <SecondaryButton
                size="small"
                className={clsx("w-7.5 !px-2", className)}
                disabled={disabled}
                onClick={onClick}
                {...rest}
            >
                {children}
            </SecondaryButton>
        </Tooltip>
    );
};

const formatObjectToType = (value: string) =>
    value
        .toLowerCase()
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
